import { Toast, ToastContainer } from '@salesforce/design-system-react';
import { type ReactNode, useSyncExternalStore } from 'react';

/**
 * App-wide notification state and the SLDS Toast viewport used by every feature.
 */
type NotificationVariant = 'error' | 'info' | 'success' | 'warning';

interface NotificationItem {
  details: ReactNode;
  duration: number;
  id: string;
  title: string;
  variant: NotificationVariant;
}

const listeners = new Set<() => void>();
let notificationItems: NotificationItem[] = [];
let nextNotificationId = 0;

const notifyListeners = () => {
  for (const listener of listeners) {
    listener();
  }
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSnapshot = () => notificationItems;

const addNotification = (notification: Omit<NotificationItem, 'id'> & { id?: string }) => {
  const id = notification.id ?? `notification-${nextNotificationId}`;
  nextNotificationId += 1;
  notificationItems = [...notificationItems.filter(item => item.id !== id), { ...notification, id }];
  notifyListeners();
};

const dismissNotification = (id: string) => {
  const remaining = notificationItems.filter(item => item.id !== id);
  if (remaining.length === notificationItems.length) return;
  notificationItems = remaining;
  notifyListeners();
};

export const ToastViewport = () => {
  const items = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  return (
    <ToastContainer>
      {items.map(({ details, duration, id, title, variant }) => (
        <Toast
          duration={duration}
          key={id}
          labels={{ details, heading: title }}
          onRequestClose={() => {
            dismissNotification(id);
          }}
          variant={variant}
        />
      ))}
    </ToastContainer>
  );
};

/**
 * Error pattern matchers
 */
const errorPatterns = [
  {
    keywords: ['fetch', 'network', 'connection'],
    message: 'Unable to connect. Please check your internet connection and try again.',
  },
  {
    keywords: ['unauthorized', '401'],
    message: 'Please log in to continue.',
  },
  {
    keywords: ['forbidden', '403', 'permission'],
    message: "You don't have permission to access this resource.",
  },
  {
    keywords: ['not found', '404'],
    message: 'The requested resource was not found.',
  },
  {
    keywords: ['rate limit', '429', 'too many'],
    message: 'Too many requests. Please wait a moment and try again.',
  },
  {
    keywords: ['invalid', 'validation', '400', 'status code 400'],
    message: 'The data you entered is not valid. Please check your input and try again.',
  },
  {
    keywords: ['500', 'server error', '503', '502', 'internal_error', 'internal error'],
    message: 'A server error occurred. Please try again later.',
  },
];

/**
 * Check if message contains technical jargon
 */
const hasTechnicalJargon = (message: string): boolean => {
  const jargon = ['undefined', 'null', 'typeerror', 'valid json'];
  return jargon.some(term => message.includes(term));
};

/**
 * Transform error message to user-friendly format
 *
 * This is where technical errors become user-friendly.
 * Add patterns to errorPatterns array to handle different scenarios.
 */
export const toUserFriendlyMessage = (error: unknown): string => {
  if (!(error instanceof Error)) {
    return 'Something went wrong. Please try again.';
  }

  const message = error.message.toLowerCase();

  // Check against known patterns
  for (const pattern of errorPatterns) {
    if (pattern.keywords.some(keyword => message.includes(keyword))) {
      return pattern.message;
    }
  }

  // Return original message if it looks user-friendly
  if (!hasTechnicalJargon(message)) {
    return error.message;
  }

  return 'Something went wrong. Please try again.';
};

/**
 * Show an error notification
 *
 * Transforms technical errors into user-friendly messages automatically.
 * Optionally provide context to make the error more actionable.
 *
 * @param error - The error object to display
 * @param context - Optional context describing what operation failed (e.g., "Failed to refresh search data")
 *
 * @example
 * // Basic usage - generic error
 * try {
 *   await fetchData();
 * } catch (error) {
 *   showErrorNotification(error);
 * }
 *
 * @example
 * // With context - specific operation
 * try {
 *   await refreshGlobalSearchData();
 * } catch (error) {
 *   showErrorNotification(error, 'Failed to refresh search data');
 * }
 *
 * @example
 * // In mutations (no context needed - mutation name provides it)
 * const mutation = useMutation({
 *   mutationFn: saveData,
 *   onError: showErrorNotification,
 * });
 */
export const showErrorNotification = (error: unknown, context?: unknown) => {
  const userMessage = toUserFriendlyMessage(error);
  const contextMessage = typeof context === 'string' ? context : undefined;

  if (import.meta.env.DEV) {
    console.error('Error:', error);
  }

  addNotification({
    details: contextMessage ? `${contextMessage}: ${userMessage}` : userMessage,
    duration: 4000,
    title: 'Error',
    variant: 'error',
  });
};

/**
 * Show a success notification
 *
 * @example
 * showSuccessNotification('Record created successfully');
 */
export const showSuccessNotification = (message: string) => {
  addNotification({
    details: message,
    duration: 4000,
    title: 'Success',
    variant: 'success',
  });
};

/**
 * Show a validation-error notification listing multiple specific problems (e.g. missing
 * required fields) rather than a single generic message — used when a user action is blocked
 * pending fixes, so they know exactly what to correct instead of just that "something's wrong".
 *
 * @example
 * showValidationErrorNotification('Cannot test formulas. Please complete all required fields.', [
 *   'Moisture → Contracted moisture (%)',
 *   'Sulfur → Lower Bound',
 * ]);
 */
export const showValidationErrorNotification = (title: string, items: string[]) => {
  addNotification({
    details: (
      <div>
        <p className="slds-text-body_small slds-m-bottom_x-small">Required fields missing:</p>
        <ul className="slds-list_dotted slds-m-left_medium">
          {items.map(item => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>
    ),
    duration: 8000,
    id: 'validation-error',
    title,
    variant: 'error',
  });
};

/**
 * Show an info notification
 *
 * @example
 * showInfoNotification('No changes to save');
 */
export const showInfoNotification = (message: string) => {
  addNotification({
    details: message,
    duration: 4000,
    title: 'Info',
    variant: 'info',
  });
};

/**
 * Show a warning notification — for preconditions the user hasn't met yet (e.g. "select
 * something first"), as opposed to showErrorNotification's "what you entered is invalid".
 *
 * @example
 * showWarningNotification('Select at least one item first.');
 */
export const showWarningNotification = (message: string) => {
  addNotification({
    details: message,
    duration: 4000,
    title: 'Warning',
    variant: 'warning',
  });
};
